import { Link } from "react-router-dom";
import {
    Table,
    TableHeader,
    TableColumn,
    TableBody,
    TableRow,
    TableCell,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Button,
    Tooltip,
} from "@heroui/react";
import { LuPlus, LuShieldCheck, LuKey, LuEye, LuPencil } from "react-icons/lu";
import { useDataTable } from "@/hooks";
import {
    EmptyState,
    ErrorState,
    ThemedText,
    PageHeader,
} from "@/components/ui";
import { useUser } from "@/context/UserContext";
import {
    DataTableControlBar,
    DataTableSkeleton,
    DataTablePagination,
} from "@/components/data-table";

const RoleData = () => {
    const { hasPermission } = useUser();
    const table = useDataTable("roles/data", null, {}, {});

    if (table.error) {
        return (
            <div className="p-6">
                <ErrorState
                    title="Gagal Memuat Data"
                    description="Terjadi kesalahan saat memuat data role. Silakan coba lagi."
                    onAction={table.refetch}
                />
            </div>
        );
    }

    const skeletonColumns = [
        { name: "No.", skeletonWidth: "w-8" },
        { name: "Nama Hak Akses", skeletonWidth: "w-40" },
        { name: "Jumlah Permission", skeletonWidth: "w-32" },
        { name: "Pengguna", skeletonWidth: "w-24" },
        { name: "Aksi", skeletonWidth: "w-20", align: "end" },
    ];

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Hak Akses"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "HR" },
                        { label: "Hak Akses" },
                    ]}
                />
                {hasPermission("role.create") && (
                    <Button
                        as={Link}
                        to="/role/create"
                        color="primary"
                        startContent={<LuPlus />}
                        size="sm"
                    >
                        Tambah Hak Akses
                    </Button>
                )}
            </div>

            {/* Data Table */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader>
                    <DataTableControlBar table={table} />
                </CardHeader>
                <CardBody className="p-0">
                    {table.isLoading ? (
                        <DataTableSkeleton
                            columns={skeletonColumns}
                            rowCount={table.pagination.perPage}
                        />
                    ) : table.items.length === 0 ? (
                        <EmptyState
                            title="Belum Ada Hak Akses"
                            description="Mulai dengan menambahkan hak akses baru."
                            icon={LuShieldCheck}
                        />
                    ) : (
                        <Table aria-label="Tabel Hak Akses" shadow="none">
                            <TableHeader>
                                <TableColumn key="no" width={50}>
                                    No.
                                </TableColumn>
                                <TableColumn key="name">
                                    Nama Hak Akses
                                </TableColumn>
                                <TableColumn key="permissions_count">
                                    Jumlah Permission
                                </TableColumn>
                                <TableColumn key="users_count">
                                    Pengguna
                                </TableColumn>
                                <TableColumn align="end" key="actions">
                                    Aksi
                                </TableColumn>
                            </TableHeader>
                            <TableBody items={table.items}>
                                {(item) => (
                                    <TableRow key={item.id}>
                                        <TableCell>
                                            <ThemedText
                                                size="sm"
                                                variant="secondary"
                                            >
                                                {(table.pagination.page - 1) *
                                                    table.pagination.perPage +
                                                    table.items.indexOf(item) +
                                                    1}
                                                .
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="sm">
                                                {item.name}
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="xs">
                                                <span className="font-medium">
                                                    {item.permissions_count}
                                                </span>{" "}
                                                permission
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <ThemedText size="xs">
                                                <span className="font-medium">
                                                    {item.users_count}
                                                </span>{" "}
                                                pengguna
                                            </ThemedText>
                                        </TableCell>
                                        <TableCell>
                                            <div className="relative flex items-center justify-end gap-2">
                                                <Tooltip content="Detail">
                                                    <Button
                                                        isIconOnly
                                                        size="sm"
                                                        variant="light"
                                                        as={Link}
                                                        to={`/role/${item.id}/detail`}
                                                    >
                                                        <LuEye
                                                            size={14}
                                                            className="text-default-500"
                                                        />
                                                    </Button>
                                                </Tooltip>
                                                {hasPermission("role.edit") && (
                                                    <Tooltip content="Edit">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/role/${item.id}/edit`}
                                                        >
                                                            <LuPencil
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                                {hasPermission("role.edit") && (
                                                    <Tooltip content="Kelola Permission">
                                                        <Button
                                                            isIconOnly
                                                            size="sm"
                                                            variant="light"
                                                            as={Link}
                                                            to={`/role/${item.id}/permissions`}
                                                        >
                                                            <LuKey
                                                                size={14}
                                                                className="text-default-500"
                                                            />
                                                        </Button>
                                                    </Tooltip>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </CardBody>
                <CardFooter>
                    <DataTablePagination table={table} />
                </CardFooter>
            </Card>
        </div>
    );
};

export default RoleData;
